import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";

import reportService, { type CustomerReportItem } from "@/services/report";
import customerService, { type Customer } from "@/services/customer";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type CustomerReportTab = "sale" | "quotation";

type CustomerReportFilters = {
  warehouse_id: number | null;
  customer_id: number | null;
  from_date: string;
  to_date: string;
  per_page: number;
};

const createInitialFilters = (): CustomerReportFilters => ({
  warehouse_id: null,
  customer_id: null,
  from_date: "",
  to_date: "",
  per_page: 10,
});

export function useCustomerReport() {
  const { t } = useI18n();
  const activeTab = ref<CustomerReportTab>("sale");
  const items = ref<CustomerReportItem[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const customers = ref<Customer[]>([]);
  const loading = ref(false);
  const exporting = ref(false);
  const searchQuery = ref("");
  const currentPage = ref(1);
  const total = ref(0);
  const filters = reactive<CustomerReportFilters>(createInitialFilters());
  const i18nScope = "customer_report_page";
  const pageTitle = `${i18nScope}.title`;

  const pagination = computed(() => ({
    current: currentPage.value,
    pageSize: filters.per_page,
    total: total.value,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
    showTotal: (total: number, range: [number, number]) =>
      t(`${i18nScope}.pagination_total`, { start: range[0], end: range[1], total }),
    locale: { items_per_page: t(`${i18nScope}.items_per_page`) },
  }));

  const summary = computed(() => ({
    grandTotal: items.value.reduce((sum, item) => sum + Number(item.invoice_total ?? 0), 0),
    paid: items.value.reduce((sum, item) => sum + Number(item.paid ?? 0), 0),
    due: items.value.reduce((sum, item) => sum + Number(item.due ?? 0), 0),
  }));

  const queryParams = computed(() => ({
    page: currentPage.value,
    per_page: filters.per_page,
    search_query: searchQuery.value || undefined,
    warehouse_id: filters.warehouse_id || undefined,
    customer_id: filters.customer_id || undefined,
    from_date: filters.from_date || undefined,
    to_date: filters.to_date || undefined,
    report_type: activeTab.value,
  }));

  const fetchFilterOptions = async () => {
    try {
      const [warehouseResults, customerResults] = await Promise.all([
        warehouseService.getWarehouses(),
        customerService.getCustomers(),
      ]);

      warehouses.value = warehouseResults;
      customers.value = customerResults;
    } catch (error) {
      console.error("Error loading customer report filters:", error);
    }
  };

  const fetchCustomerReport = async () => {
    loading.value = true;

    try {
      const response = await reportService.getCustomerReports(queryParams.value);
      items.value = response.data ?? [];
      currentPage.value = response.current_page;
      total.value = response.total;
    } catch (error) {
      console.error("Error fetching customer report:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.load_failed`),
        text: t(`${i18nScope}.unable_to_load_report`),
      });
    } finally {
      loading.value = false;
    }
  };

  const setActiveTab = (tab: CustomerReportTab) => {
    if (activeTab.value === tab) return;

    activeTab.value = tab;
    currentPage.value = 1;
    fetchCustomerReport();
  };

  const handleTableChange = (page: number, pageSize: number) => {
    currentPage.value = page;

    if (pageSize !== filters.per_page) {
      filters.per_page = pageSize;
      currentPage.value = 1;
    }

    fetchCustomerReport();
  };

  const applyFilters = () => {
    currentPage.value = 1;
    fetchCustomerReport();
  };

  const resetFilters = () => {
    Object.assign(filters, createInitialFilters());
    currentPage.value = 1;
    fetchCustomerReport();
  };

  const exportCustomerReport = async () => {
    exporting.value = true;

    try {
      const response = await reportService.exportCustomerReports({
        search_query: searchQuery.value || undefined,
        warehouse_id: filters.warehouse_id || undefined,
        customer_id: filters.customer_id || undefined,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined,
        report_type: activeTab.value,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `customer_${activeTab.value}_report_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting customer report:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.export_failed`),
        text: t(`${i18nScope}.unable_to_export_report`),
      });
    } finally {
      exporting.value = false;
    }
  };

  watch(searchQuery, () => {
    currentPage.value = 1;
    fetchCustomerReport();
  });

  onMounted(async () => {
    await fetchFilterOptions();
    await fetchCustomerReport();
  });

  return {
    pageTitle,
    i18nScope,
    activeTab,
    items,
    warehouses,
    customers,
    loading,
    exporting,
    searchQuery,
    filters,
    pagination,
    summary,
    setActiveTab,
    handleTableChange,
    applyFilters,
    resetFilters,
    exportCustomerReport,
  };
}
